import React, { useEffect } from 'react'
import { connect, useDispatch } from 'react-redux'

import { setProjectIdentity } from '@store/actionSlices/projectIdentity'
import { setShowcaseRoomConfig } from '@store/actionSlices/showcase-room'
import { TokenInterface } from '@store/actionSlices/token'
import { Template } from '@store/constants'
import { ProjectIdentity, RootStateTypeExtra, User } from '@store/types'

import {
  selectFromResult as selectFromConfigResult,
  useGetConfigQuery,
} from '@api/config'
import { ShowcaseRoomConfig } from '@api/showcase-room'

import useGetShowcaseRoomsConfig from '@hooks/useGetShowcaseRoomConfig'

import useTemplateHandler from './use-template-handler'

interface Props {
  projectIdentity: ProjectIdentity
  token: TokenInterface
  showcaseRoomConfig: ShowcaseRoomConfig
  v1: React.ReactNode
  v2: React.ReactNode
  user: User
}

function TemplateHandler({
  projectIdentity,
  token,
  v1,
  v2,
  showcaseRoomConfig,
  user,
}: Props) {
  const dispatch = useDispatch()

  const {
    isLoaded,
    isError,
    showcaseRoomConfig: showcaseRoomConfigApiData,
    isFulfilled,
  } = useGetShowcaseRoomsConfig({
    projectName: projectIdentity.projectName,
    token,
  })

  const {
    configData,
    isLoaded: isConfigLoaded,
    isError: configHasError,
  } = useGetConfigQuery(
    { projectName: projectIdentity.projectId },
    {
      selectFromResult: selectFromConfigResult,
      skip: !projectIdentity.projectId,
    }
  )

  useEffect(() => {
    if (isLoaded && !isError && isFulfilled && showcaseRoomConfigApiData) {
      dispatch(setShowcaseRoomConfig(showcaseRoomConfigApiData))
    }
  }, [isLoaded, isError, dispatch, isFulfilled])

  useEffect(() => {
    if (
      isConfigLoaded &&
      !configHasError &&
      !!user?.email &&
      projectIdentity.projectId
    ) {
      dispatch(
        setProjectIdentity({
          ...projectIdentity,
          ...configData,
          ...showcaseRoomConfig,
        })
      )
    }
  }, [
    isConfigLoaded,
    showcaseRoomConfig,
    user?.email,
    projectIdentity.projectId,
  ])

  const selectedTemplate = useTemplateHandler({
    projectIdentity,
    token,
  })

  if (!selectedTemplate) {
    return null
  }

  switch (selectedTemplate) {
    case Template.V2:
      return <>{v2}</>
    default:
      return <>{v1}</>
  }
}

export default connect(
  ({
    projectIdentity,
    token,
    showcaseRoom: showcaseRoomConfig,
    user,
  }: RootStateTypeExtra) => ({
    projectIdentity,
    token,
    showcaseRoomConfig,
    user,
  })
)(TemplateHandler)
